import { useTranslation } from "react-i18next";

import type { SharedRebateProgramsStats } from "../../../api/generated";

import { SkeletonLoader } from "../../../SkeletonLoader";
import { TooltipWrapper } from "../../../Tooltip";
import { Typo } from "../../../Typo";
import { fmtInt, fmtVolExact, fmtVolMlnParts } from "../data";
import { MobileInfoTrigger } from "./MobileInfoTrigger";

type MobileKpiRailProps = {
  stats: SharedRebateProgramsStats | undefined;
  isStatsError: boolean;
  /** Full reload (search/filter/sort/page change, or first load) — every card skeletons. */
  isFullReload: boolean;
  /** Payout-period-only reload — only the money cards skeleton (see `periodOnly` cards below). */
  isPeriodReload: boolean;
};

type Card = {
  key: string;
  label: string;
  value: string;
  unit?: string;
  info?: string;
  /**
   * Exact-figure tap tooltip on the value, set only when `value` is a
   * shortened ("mln"/"bln") amount.
   */
  tooltip?: string;
  /** Reloads on a payout-period change, not just a full reload — Total earnings / Your payout. */
  periodScoped?: boolean;
};

/**
 * Horizontal-scroll KPI strip for the mobile Shared Rebate Programs tab —
 * same 5 metrics as the desktop `StatCard` strip (Active programs, Client
 * accounts, Unique clients, Total earnings, Your payout), same
 * period-vs-full reload split. Structural pattern mirrors
 * `PerformanceReport/mobile/MobileSummaryCards.tsx`; card token per the
 * PART-4052 handoff spec §3 ("Metric card": `rounded-[16px]`,
 * `bg-surface-brand-subtle`).
 */
export const MobileKpiRail = ({
  stats,
  isStatsError,
  isFullReload,
  isPeriodReload,
}: MobileKpiRailProps) => {
  const { t } = useTranslation();

  const cards: Card[] = [
    {
      key: "activePrograms",
      label: t("subIBReport_statActivePrograms", "Active programs"),
      value: fmtInt(stats?.activePrograms ?? 0),
    },
    {
      key: "clientAccounts",
      label: t("subIBReport_statClientAccounts", "Client accounts"),
      value: fmtInt(stats?.clientAccounts ?? 0),
    },
    {
      key: "uniqueClients",
      label: t("subIBReport_statUniqueClients", "Unique clients"),
      value: fmtInt(stats?.uniqueClients ?? 0),
    },
    {
      key: "totalEarning",
      label: t("subIBReport_statTotalEarning", "Total earnings"),
      ...fmtVolMlnParts(stats?.totalEarning ?? 0),
      tooltip:
        Math.abs(stats?.totalEarning ?? 0) >= 1e6
          ? fmtVolExact(stats?.totalEarning ?? 0)
          : undefined,
      info: t(
        "subIBReport_statTotalEarningInfo",
        "Gross rebate earned from all clients' trading activity before split in the selected time period.",
      ),
      periodScoped: true,
    },
    {
      key: "yourPayout",
      label: t("subIBReport_statYourPayout", "Your payout"),
      ...fmtVolMlnParts(stats?.yourPayout ?? 0),
      tooltip:
        Math.abs(stats?.yourPayout ?? 0) >= 1e6
          ? fmtVolExact(stats?.yourPayout ?? 0)
          : undefined,
      info: t(
        "subIBReport_statYourPayoutInfo",
        "Total earnings minus what's shared with your Sub IBs in the selected time period.",
      ),
      periodScoped: true,
    },
  ];

  return (
    <div className="scrollbar-minimal-hover -mx-1 overflow-x-auto px-1">
      <div
        className="grid auto-cols-[minmax(9rem,1fr)] grid-flow-col gap-3"
        style={{ minWidth: "640px" }}
      >
        {cards.map((card) => {
          const skeleton =
            isFullReload || (isPeriodReload && card.periodScoped);
          return (
            <div
              key={card.key}
              className="flex flex-col gap-1 rounded-[16px] bg-surface-brand-subtle p-3"
              data-testid={`mobile-kpi-${card.key}`}
            >
              <span className="flex items-center gap-1">
                <Typo
                  element="span"
                  type="bodyXS"
                  textColor="text-text-secondary"
                >
                  {card.label}
                </Typo>
                {card.info && (
                  <MobileInfoTrigger label={card.label} text={card.info} />
                )}
              </span>
              {skeleton ? (
                <SkeletonLoader width={72} height={20} variant="rounded" />
              ) : isStatsError ? (
                <Typo
                  element="span"
                  type="labelMD"
                  textColor="text-text-disabled"
                >
                  --
                </Typo>
              ) : card.tooltip ? (
                <TooltipWrapper
                  text={card.tooltip}
                  color="blue"
                  openEvents={{ click: true }}
                  // This rail scrolls horizontally inside its own
                  // `overflow-x-auto` wrapper below — without "fixed",
                  // react-tooltip's absolute-positioned panel gets
                  // clipped/cut off by that ancestor instead of floating
                  // above it.
                  positionStrategy="fixed"
                  tooltipPlacement="top"
                  tooltipMaxWidth={240}
                  tooltipContainerClassName="!text-xs"
                >
                  <span className="flex items-baseline gap-1">
                    <Typo
                      element="span"
                      type="labelMD"
                      textColor="text-text-primary"
                    >
                      {card.value}
                    </Typo>
                    {card.unit && (
                      <Typo
                        element="span"
                        type="labelXS"
                        textColor="text-text-secondary"
                      >
                        {card.unit}
                      </Typo>
                    )}
                  </span>
                </TooltipWrapper>
              ) : (
                <span className="flex items-baseline gap-1">
                  <Typo
                    element="span"
                    type="labelMD"
                    textColor="text-text-primary"
                  >
                    {card.value}
                  </Typo>
                  {card.unit && (
                    <Typo
                      element="span"
                      type="labelXS"
                      textColor="text-text-secondary"
                    >
                      {card.unit}
                    </Typo>
                  )}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
